<template>
  <div class="base-date-picker">
    <!-- Phones: the picker in a sheet (docs/page-layout.md), across the screen. -->
    <template v-if="isPhone">
      <button type="button" class="base-date-picker__display" aria-haspopup="dialog" @click="menu = true">
        <BaseIcon name="calendar" />
        {{ placeholder }}
      </button>
      <div class="base-date-picker__navigation">
        <BaseButton class="base-date-picker__nav-button" isTransparent isSmall @click.native="onPrevious"
          ><BaseIcon name="chevron-left"
        /></BaseButton>
        <BaseButton class="base-date-picker__nav-button" isTransparent isSmall @click.native="onNext"
          ><BaseIcon name="chevron-right"
        /></BaseButton>
      </div>
      <BaseBottomSheet v-model="menu" :title="$t('date-picker.placeholder')">
        <DatePicker
          v-if="menu"
          :propDates="dates"
          range
          @on-close-menu="closeMenu"
          @on-save-dates="saveDates"
          :variant="variant"
          :datePickerValues="datePickerValues"
        />
      </BaseBottomSheet>
    </template>
    <v-menu
      v-else
      ref="datePickerMenu"
      :close-on-content-click="false"
      :return-value.sync="dates"
      transition="scale-transition"
      offset-y
      nudge-top="-2"
      :content-class="menuContentClass"
      v-model="menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="base-date-picker__display" v-on="on" v-bind="attrs">
          <BaseIcon name="calendar" />
          {{ placeholder }}
        </div>
        <div class="base-date-picker__navigation">
          <BaseButton class="base-date-picker__nav-button" isTransparent isSmall @click.native="onPrevious"
            ><BaseIcon name="chevron-left"
          /></BaseButton>
          <BaseButton class="base-date-picker__nav-button" isTransparent isSmall @click.native="onNext"
            ><BaseIcon name="chevron-right"
          /></BaseButton>
        </div>
      </template>
      <div class="base-date-picker__card">
        <div class="base-date-picker__prepend-item" v-if="isMobile">
          <div class="base-date-picker__close-icon">
            <BaseIcon @click="closeMenu" name="x-circle" />
          </div>
        </div>
        <DatePicker
          :propDates="dates"
          range
          @on-close-menu="closeMenu"
          @on-save-dates="saveDates"
          :variant="variant"
          :datePickerValues="datePickerValues"
        />
      </div>
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment';
import { getPreviousPeriod, getNextPeriod } from '@/util/date-util';
import BaseIcon from '@/components/base/BaseIcon';
import BaseButton from '@/components/base/BaseButton';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import DatePicker from '@/components/form/DatePicker';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import { DATE_PICKER_PERIOD } from '@/util/constants/date-picker-periods';
import { viewport } from '@/util/viewport';

export default {
  name: 'BaseDatePicker',
  mixins: [IsMobileMixin],
  components: {
    BaseIcon,
    BaseButton,
    BaseBottomSheet,
    DatePicker,
  },
  props: {
    variant: {
      type: String,
      default: 'standard', // <light, standard, full>
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    datePickerValues: {
      type: Array,
      required: false,
      default: () => DATE_PICKER_PERIOD,
    },
  },
  data() {
    return {
      menu: false,
      dates: [],
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    placeholder() {
      return this.dates[0] + ' - ' + this.dates[1];
    },
    menuContentClass() {
      if (this.isMobile) {
        return 'v-menu__content mobile';
      }
      return 'base-date-picker__menu';
    },
  },
  mounted() {
    this.dates = [this.startDate, this.endDate];
  },
  methods: {
    closeMenu() {
      this.menu = false;
    },
    saveDates(dates) {
      this.dates = dates;
      if (this.$refs.datePickerMenu) this.$refs.datePickerMenu.save(dates);

      this.$emit('on-save-dates', dates);
      this.closeMenu();
    },
    onPrevious() {
      this.dates = getPreviousPeriod(moment(this.dates[0]), moment(this.dates[1]));
      this.$emit('on-save-dates', this.dates);
    },
    onNext() {
      this.dates = getNextPeriod(moment(this.dates[0]), moment(this.dates[1]));
      this.$emit('on-save-dates', this.dates);
    },
  },
};
</script>

<style lang="scss">
.base-date-picker {
  border: 1px solid $color-border;
  border-radius: $border-radius-root;
  color: $color-normal;
  display: flex;
  align-items: center;

  &__card {
    margin: auto;
    width: max-content;
    background-color: $color-white;
  }

  .base-icon {
    width: 16px;
    height: 16px;
    color: $color-normal-pale;
  }

  &__display {
    display: flex;
    align-items: center;
    padding: 8px;
    padding-right: 16px;
    width: max-content;
    cursor: pointer;
    transition: 200ms ease;
    gap: 8px;
    font-size: $font-size-small;
    font-weight: 600;

    &:hover {
      background: $color-hover;
      transition: 200ms ease;
    }

    @include screen-below('tablet') {
      min-height: 44px;
    }
  }

  &__placeholder {
    margin-left: 8px;
    font-size: 16px;
  }

  &__navigation {
    display: flex;
    flex-grow: 1;
    align-self: stretch;
  }

  &__nav-button {
    cursor: pointer;
    display: flex;
    align-items: center;
    height: 100%;
    flex-grow: 1;
    border-left: 1px solid $color-border;
    transition: 200ms ease;

    &:hover {
      background: $color-hover;
      transition: 200ms ease;
    }

    &:nth-child(2) {
      border-radius: 0 $border-radius-medium $border-radius-medium 0;
    }
  }

  // Mobile: sticky close (X) row above the picker.
  &__prepend-item {
    position: sticky;
    background: transparent;
    z-index: $base-index;
    width: 100%;
    top: 0;
    display: flex;
    justify-content: flex-end;
    min-height: 32px;
    padding-top: 8px;
  }

  &__close-icon {
    color: #bdc9d6;
    height: 28px;
    margin-right: 8px;
    width: 28px;

    &:hover {
      cursor: pointer;
    }
  }
}

// Mobile: the picker menu goes full-screen. Teleported to <body>, so it must be a global
// selector.
.v-menu__content.mobile {
  border-radius: 0 !important;
  z-index: $top-index !important;
  max-height: 100% !important;
  height: 100%;
  width: 100% !important;
  max-width: 100% !important;
  position: absolute;
  top: 0 !important;
  left: 0 !important;
  background: rgba(77, 92, 116, 0.25);
  padding: 32px;
  align-items: center;
  transition: none !important;
}
</style>
